import * as React from 'react';
import DashedLine from 'react-native-dashed-line';
import {withGlobalize} from 'react-native-globalize';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import ScreenContext from '../../ScreenContext';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import {getTaxDescription} from 'src/services/TranslationService';
import AVFormattedCurrency from '../../elements/AVFormattedCurrency';
import ReceiptItem from '../../elements/cart/ReceiptItem';
import RoundedButton, {ButtonType} from '../../elements/RoundedButton';
import Styles from '../../Styles';
import BaseSummaryScreen from '../account/history/BaseSummaryScreen';
import Settings from 'src/Settings';
import AVText from '../../elements/AVText';
import {PixelRatio, StyleSheet, View} from 'react-native';
import BuildTypes from 'src/constants/BuildTypeConstants';
import Localized from 'src/constants/AppStrings';
type ReceiptScreenProps = {
  returnRoute: AppRoutes;
  balance: number;
  ccDisplay: string;
  totalDue: number;
  subtotal: number;
  discount: number;
  totalAmt: number;
  taxes: Array<any>;
  deposit: number;
  items: Array<any>;
  points: number;
  split: boolean;
  insideTab: boolean;
  deviceId: string;
};

class ReceiptScreen extends React.Component<ReceiptScreenProps> {
  static contextType = ScreenContext;

  constructor(props) {
    super(props);
    this.onFeedbackSelect = this.onFeedbackSelect.bind(this);
    this.renderSummary = this.renderSummary.bind(this);
  }

  onFeedbackSelect() {
    NavActions.replace(AppRoutes.ContactUs, {
      subject: 'Feedback',
      popToTop: true,
      insideTab: this.props.insideTab,
      deviceId: this.props.deviceId,
    });
  }

  renderSummary() {
    const labels = [];
    const amounts = [];

    if (this.props.subtotal > 0) {
      labels.push(
        <AVText style={Styles.Style.summaryLabel} key="subtotal">
          {Localized.Labels.subtotal}
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          accessibilityLabel="subtotal"
          key="subtotal"
          style={Styles.Style.summaryLabel}
          value={this.props.subtotal}
        />,
      );
    }

    if (this.props.discount > 0) {
      labels.push(
        <AVText
          style={[Styles.Style.summaryLabel, styles.discount]}
          key="discount"
        >
          {Localized.Labels.discount}
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          value={this.props.discount * -1}
          key="discount"
          style={[Styles.Style.summaryLabel, styles.discount]}
          accessibilityLabel="discount"
        />,
      );
    }

    if (this.props.taxes && this.props.taxes.length > 0) {
      for (let i = 0; i < this.props.taxes.length; i++) {
        const tax = this.props.taxes[i];
        labels.push(
          <AVText style={Styles.Style.summaryLabel} key={`tax${i}`}>
            {getTaxDescription(Localized, tax.Description)}
          </AVText>,
        );
        amounts.push(
          <AVFormattedCurrency
            value={parseFloat(tax.TaxAmount)}
            style={Styles.Style.summaryLabel}
            accessibilityLabel={tax.Description}
            key={`tax${i}`}
          />,
        );
      }
    }

    if (this.props.deposit > 0) {
      labels.push(
        <AVText style={Styles.Style.summaryLabel} key="deposit">
          {Localized.Labels.deposit}
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          style={Styles.Style.summaryLabel}
          value={this.props.deposit}
          accessibilityLabel="deposit"
          key="deposit"
        />,
      );
    }

    if (this.props.split) {
      if (this.props.split) {
        let label = Localized.Labels.account_balance;

        if (Settings.buildType === BuildTypes.pantry) {
          label = Localized.Labels.remaining_credit;
        }

        labels.push(
          <AVText style={Styles.Style.summaryLabel} key="remaininBalance">
            {label}
          </AVText>,
        );
        amounts.push(
          <AVFormattedCurrency
            style={Styles.Style.summaryLabel}
            value={this.props.balance * -1}
            accessibilityLabel="remainingBalance"
            key="remaininBalance"
          />,
        );
      }
    }

    if (this.props.split) {
      labels.push(
        <AVText style={Styles.Style.summaryLabel} key="totalDue">
          {Localized.Labels.total_due} ({this.props.ccDisplay})
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          style={Styles.Style.summaryLabel}
          value={this.props.totalDue}
          accessibilityLabel="totalDue"
          key="totalDue"
        />,
      );
    } else {
      labels.push(
        <AVText style={Styles.Style.summaryTotalLabel} key="due">
          {Localized.Labels.total}
        </AVText>,
      );
      amounts.push(
        <AVFormattedCurrency
          style={Styles.Style.summaryTotalLabel}
          accessibilityLabel="due"
          key="due"
          value={this.props.totalAmt}
        />,
      );
    }

    return (
      <View style={Styles.Style.summarySection}>
        <View style={Styles.Style.labelColumn}>{labels}</View>
        <View style={Styles.Style.amountColumn}>{amounts}</View>
      </View>
    );
  }

  render() {
    let i = 0;
    const receiptItems = this.props.items.map((item) => (
      <ReceiptItem
        desc={item.desc}
        amount={item.price}
        key={i++}
        accessibilityLabel="itemPrice"
      />
    ));
    return (
      <BaseSummaryScreen
        strings={Localized}
        titleText={Localized.Buttons.receipt}
        returnRoute={this.props.returnRoute}
        accessibilityLabel="Receipt Title"
      >
        <DashedLine
          dashGap={PixelRatio.roundToNearestPixel(4)}
          dashLength={PixelRatio.roundToNearestPixel(2)}
          dashThickness={PixelRatio.roundToNearestPixel(2)}
          dashColor="#999"
          style={styles.dashStyle}
        />
        {receiptItems}
        <DashedLine
          dashGap={PixelRatio.roundToNearestPixel(4)}
          dashLength={PixelRatio.roundToNearestPixel(2)}
          dashThickness={PixelRatio.roundToNearestPixel(2)}
          dashColor="#999"
          style={[
            styles.dashStyle,
            {
              marginTop: -1 * Styles.Spacing.m2,
            },
          ]}
        />
        {this.renderSummary()}
        <RoundedButton
          buttonType={ButtonType.outline}
          containerStyle={styles.button}
          onPress={this.onFeedbackSelect}
          accessibilityLabel="Report Problem"
          text={Localized.Labels.report_problem}
        />
      </BaseSummaryScreen>
    );
  }
}

const styles = StyleSheet.create({
  button: {
    marginTop: Styles.Spacing.m4,
  },
  dashStyle: {
    borderRadius: 100,
    height: PixelRatio.roundToNearestPixel(2),
    marginBottom: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m2,
    overflow: 'hidden',
    width: '100%',
  },
  discount: {
    color: Styles.positiveColor,
  },
});
export default withForwardedNavigationParams()(withGlobalize(ReceiptScreen));
